import type { ConfigService } from '../App/services/mx-config-service';
import type { AuthBehaviorProviderService } from './mx-auth-behavior-provider-service';
import type { HttpService } from '../App/services/http-service';
import type { I18n } from '../i18n/mx-internationalization';
import type { material } from 'angular';
import type { LoginParams } from './auth.contracts';
import type { SessionModel } from './session.model';
import { AuthHelper } from './auth.helper';

/**
 * @ngdoc service
 * @name mx.shell.AuthService
 * @module mx.shell
 * @description Represents basic logic for an application authentication
 */
export class AuthenticationService {
	private session?: SessionModel;
	private loginPromise: Promise<SessionModel> | null = null;
	private _logoutPromise: Promise<void> | null = null;
	private refreshTokenCallback?: () => Promise<string>;
	private refreshPromise?: Promise<boolean>;
	private helper: AuthHelper;
	private loginParams?: LoginParams;
	private orgUnitId?: string;

	static $inject = ['mx.shell.Config', 'mx.shell.AuthBehaviorProviderService', 'mx.SolutionBuilderAgent.Http', 'mx.internationalization', '$mdDialog'];

	constructor(
		private readonly mxConfig: ConfigService,
		private readonly authBehavior: AuthBehaviorProviderService,
		private readonly http: HttpService,
		private readonly i18n: I18n,
		private readonly $mdDialog: material.IDialogService
	) {
		this.helper = this.getAuthHelper();
	}

	async doRefreshLogin(accessToken: string) {
		if (this.session) {
			this.session.refresh(accessToken);
		}
	}

	async onRefreshToken(callback: () => Promise<string>) {
		if (typeof callback !== 'function') {
			throw new Error('onRefreshSession callback must be a function');
		}
		this.refreshTokenCallback = callback;
	}

	async checkForTokenExpirationAndRefresh() {
		this.refreshPromise = this.refreshPromise || this._checkForTokenExpirationAndRefresh();
		const result = await this.refreshPromise;
		delete this.refreshPromise;
		return result;
	}

	private async _checkForTokenExpirationAndRefresh() {
		let expTime = this.getSessionExpirationTime();
		if (expTime) {
			expTime -= 60000; // 1 minute before expiration time
			if (expTime < Date.now()) {
				if (this.refreshTokenCallback) {
					try {
						const token = await this.refreshTokenCallback();
						if (token) {
							await this.doRefreshLogin(token);
							return true;
						}
					} catch (error) {
						console.error('Error refreshing token', error);
						return false;
					}
				}
			} else {
				return true;
			}
		}
		return false;
	}

	private async onSessionExpired(session: SessionModel) {
		const { authBehavior, $mdDialog, i18n } = this;

		const wasTokenRefresh = await this.checkForTokenExpirationAndRefresh();
		if (wasTokenRefresh) {
			return;
		}
		delete this.session;
		await authBehavior.sessionExpired();

		const confirm = $mdDialog
			.confirm()
			.title(i18n.get('shell.authentication.sessionExpired'))
			.ariaLabel(i18n.get('shell.authentication.sessionExpired'))
			.ok(i18n.get('shell.authentication.confirmGoToLogin'))
			.cancel(i18n.get('shell.authentication.stayHereAnyway'));

		$mdDialog.show(confirm).then(() => {
			this.helper.startLogoutWorkflow(session, true, this.orgUnitId);
		});
	}

	private async loginFLow(directToken?: string) {
		const session = await this.helper.makeSessionAvailable(directToken);

		if (!this.orgUnitId && session?.ouID) {
			this.initOrgUnit(session.ouID);
		}

		const wrongOrgUnit = (this.orgUnitId && session && session.ouID !== this.orgUnitId) || false;

		if (!session || !session.verify() || wrongOrgUnit) {
			if (this.helper.areReturningFromExternalAuthorizationWithPKCE() || this.helper.areReturningFromExternalAuthorization() || wrongOrgUnit) {
				await this.logout();
			} else {
				this.helper.startAuthorizeWorkflow(this.orgUnitId, true);
				await new Promise((resolve) => setTimeout(resolve, 5000)); //wait for redirection to login page without error
			}
			throw new Error('Failed with redirection to login');
		}

		this.session = session;
		this.authBehavior.sessionEstablished(session, !session.restored);

		session.onDestroyed((isExpired) => isExpired && this.onSessionExpired(session));

		return session;
	}

	public async login(directToken?: string) {
		if (this.mxConfig.isDebugMode) {
			return;
		}
		return (this.loginPromise ||= this.loginFLow(directToken));
	}

	private async logoutFlow() {
		if (this.session) {
			await this.authBehavior.beforeSessionDestroy();
		}

		this.helper.startLogoutWorkflow(this.session, false, this.orgUnitId);
		delete this.session;
		await new Promise((resolve) => setTimeout(resolve, 5000)); //wait for redirection to login page without error

		this._logoutPromise = null;
	}

	public async logout() {
		this._logoutPromise = this._logoutPromise || this.logoutFlow();
		return this._logoutPromise;
	}

	async getToken() {
		return this.session?.raw;
	}

	getSessionExpirationTime() {
		return this.session?.expirationTime || null;
	}

	async isAuthenticated() {
		return Boolean(await this.getToken());
	}

	getAuthConfig() {
		return this.mxConfig.get('authentication') ?? {};
	}

	public async getLoginParams() {
		const authCfg = this.getAuthConfig();
		return (this.loginParams ||= await this.http.get<LoginParams>(`/api/login/params?clientId=${authCfg.oauth2.clientid}${this.orgUnitId ? `&ou=${this.orgUnitId}` : ''}`));
	}

	async isSignUpEnabled() {
		const authParams = await this.getLoginParams();
		return Boolean(authParams.AllowUserRegistration);
	}

	private getAuthHelper() {
		const authCfg = this.getAuthConfig();
		const oauth2 = authCfg.oauth2;
		oauth2.redirect_uri = this.http.fixRelationLink(oauth2.redirect_uri);
		oauth2.endpoints = oauth2.endpoints || {};
		oauth2.endpoints.authorize = this.http.fixRelationLink(oauth2.endpoints.authorize);
		oauth2.endpoints.stsLogout = this.http.prepareUrl('/api/sts/logout');
		return new AuthHelper(authCfg);
	}

	public initOrgUnit(orgUnitId: string) {
		this.orgUnitId = orgUnitId;
	}
}
